/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */
import type { Theme } from "@mui/material/styles";

// Heading style values shared across every "layer info" surface
// (legend inline panel, popover, mobile dialog). Apply with whichever
// selector targets the h1-h6 elements inside that surface — the legend
// uses `&& h1, ...` while the popover/dialog scope it to
// `.MuiDialogContent-root` to avoid restyling DialogTitle (rendered as h2).
export const layerInfoHeadingValuesSx = (theme: Theme) => ({
  fontSize: "0.85rem",
  fontWeight: 600,
  marginTop: theme.spacing(1.25),
  marginBottom: theme.spacing(0.5),
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  color: theme.palette.text.secondary,
});

// Chip styling shared between the legend inline info panel and the popover.
export const layerInfoChipSx = {
  "&& .MuiChip-root": {
    fontSize: "0.7rem",
    height: 20,
  },
};

// Body font size used across compact layer-info surfaces (popover Paper and
// legend inline panel). Kept as a string so it can be dropped straight into
// MUI's `sx` `fontSize` fields.
export const LAYER_INFO_FONT_SIZE = "0.8125rem";

// Slightly larger body font for the wider admin surfaces (layer detail page and
// edit preview tab), which want the map's heading/layout styling but a touch
// more readable body text than the compact map panels.
export const LAYER_INFO_FONT_SIZE_ADMIN = "0.875rem";

// Base text styling for compact layer-info surfaces. Applies the given body
// font size to the container and to every common text descendant so nested HTML
// from `ConsolidatedDescription` inherits consistently. Also forces aggressive
// word-wrap so long URLs don't overflow.
const makeLayerInfoTextSx = (fontSize: string) => ({
  fontSize,
  wordBreak: "break-word" as const,
  overflowWrap: "anywhere" as const,
  "&& p, && li, && a, && span, && code, && .MuiTypography-root, && .MuiAlert-message":
    {
      fontSize,
      lineHeight: 1.5,
      overflowWrap: "anywhere" as const,
      wordBreak: "break-word" as const,
    },
});

export const layerInfoTextSx = makeLayerInfoTextSx(LAYER_INFO_FONT_SIZE);

// Tables rendered inside layer-info surfaces. Columns size to whole words
// (no mid-word breaks), with light grid borders and modest cell padding.
// Shared between the popover/dialog and the legend inline info panel.
//
// ELSA result tables (`.marapp-qa-elsa-result-info`) ship their own bespoke
// styling, so we exclude their descendants from every selector below.
const NOT_ELSA = ":not(.marapp-qa-elsa-result-info *)";
export const layerInfoTableSx = {
  // The user-agent stylesheet gives <figure> a 40px inline margin which
  // wastes most of the narrow popover/legend width.
  "& figure.table": { margin: 0 },
  [`& table${NOT_ELSA}`]: {
    borderCollapse: "collapse" as const,
    width: "100%",
  },
  [`& thead${NOT_ELSA}`]: { textAlign: "left" as const },
  [`& th${NOT_ELSA}, & td${NOT_ELSA}`]: {
    paddingLeft: 0.5,
    paddingRight: 0.5,
    border: "1px solid rgba(255, 255, 255, 0.25)",
    wordBreak: "normal" as const,
    overflowWrap: "normal" as const,
  },
};

// Combined container styling for any surface that wraps a
// `ConsolidatedDescription` and wants the compact "layer info" look from the
// map (legend inline panel, popover) — shared text sizing, table styling,
// section-heading styling and chip styling. The `&&` heading selector beats the
// `Html` component's default `theme.typography.h6` so section titles render as
// the small uppercase headings used on the map rather than large headings.
//
// Pass `fontSize` to override the body text size (e.g. the admin surfaces use a
// slightly larger size than the compact map panels); headings are unaffected.
export const layerInfoContainerSx = (
  theme: Theme,
  options?: { fontSize?: string }
) => ({
  ...makeLayerInfoTextSx(options?.fontSize ?? LAYER_INFO_FONT_SIZE),
  ...layerInfoTableSx,
  "&& h1, && h2, && h3, && h4, && h5, && h6": layerInfoHeadingValuesSx(theme),
  ...layerInfoChipSx,
});
